import { MaterialStageEnum } from "@/types/Views/MappingWizard/MaterialStageEnum";
import type { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";

import type { ColourKey, ColourKeyCategory } from "./colourKeys";
import { includedLayers } from "./keyLayers";

export type MaterialStage = "new" | "existing" | "demolition" | "mixed";

export const MATERIAL_STAGE_ORDER: MaterialStage[] = ["new", "existing", "demolition", "mixed"];

// Scene colours, mirroring the material-stage-* tokens in style.css; kept off the mapping tool's green/yellow/red.
export const MATERIAL_STAGE_COLOURS: Record<MaterialStage, string> = {
  new: "#0d9488",
  existing: "#64748b",
  demolition: "#d3790c",
  mixed: "#8954c9",
};

export const MATERIAL_STAGE_SWATCH_CLASSES: Record<MaterialStage, string> = {
  new: "bg-material-stage-new",
  existing: "bg-material-stage-existing",
  demolition: "bg-material-stage-demolition",
  mixed: "bg-material-stage-mixed",
};

export const MATERIAL_STAGE_LABELS: Record<MaterialStage, string> = {
  new: "New construction",
  existing: "Existing",
  demolition: "Demolition",
  mixed: "Mixed",
};

const STAGE_BY_ENUM: Record<number, MaterialStage> = {
  [MaterialStageEnum["New Construction"]]: "new",
  [MaterialStageEnum.Existing]: "existing",
  [MaterialStageEnum.Demolished]: "demolition",
};

// An unmapped layer has no stage that counts; a mapped layer without a stored one is new construction.
export function layerStage(layer: SpeckleMaterial): MaterialStage | null {
  if (layer.mapping == null) return null;
  return STAGE_BY_ENUM[layer.stage ?? MaterialStageEnum["New Construction"]] ?? null;
}

export const stageCombine = (first: string, second: string): string => (first === second ? first : "mixed");

const MATERIAL_STAGE_CATEGORIES: ColourKeyCategory[] = MATERIAL_STAGE_ORDER.map((stage) => ({
  id: stage,
  label: MATERIAL_STAGE_LABELS[stage],
  colour: MATERIAL_STAGE_COLOURS[stage],
  swatchClass: MATERIAL_STAGE_SWATCH_CLASSES[stage],
}));

const SUB_LINE_WORDING: Record<string, string> = {
  new: "new construction",
  existing: "existing",
  demolition: "demolition",
};

const SUB_LINE_SHORT: Record<MaterialStage, string> = {
  new: "new",
  existing: "existing",
  demolition: "demolition",
  mixed: "mixed",
};

// "1 of 3 layers existing" over included, mapped layers; Mixed lists each stage's count in display order.
export function stageSubLine(type: SpeckleMaterial, category: string): string {
  const layers = includedLayers(type).filter((layer) => layer.mapping != null);
  if (category === "mixed") {
    return MATERIAL_STAGE_ORDER.map((stage) => ({ stage, count: layers.filter((layer) => layerStage(layer) === stage).length }))
      .filter((entry) => entry.count > 0)
      .map((entry) => `${entry.count} ${SUB_LINE_SHORT[entry.stage]}`)
      .join(", ");
  }
  const matches = layers.filter((layer) => layerStage(layer) === category).length;
  const wording = SUB_LINE_WORDING[category] ?? category;
  return `${matches} of ${layers.length} ${layers.length === 1 ? "layer" : "layers"} ${wording}`;
}

export const MATERIAL_STAGE_KEY = {
  id: "materialStage" as const,
  label: "Material stage",
  group: "mapping",
  hint: "Each type shows its material layers' stage; layers that disagree paint as Mixed.",
  categoriesFor: () => MATERIAL_STAGE_CATEGORIES,
  combine: stageCombine,
  classifyLayer: layerStage,
  subLine: stageSubLine,
} satisfies ColourKey;
